import { useEffect, useRef } from "react";

/**
 * What every concrete player reports back to `StationPlayer`.
 * `network` tells the recovery policy whether a reconnect is worth trying.
 */
export interface PlayerCallbacks {
  onPlaying?: () => void;
  onFatal?: (err: { message: string; network: boolean }) => void;
  /** The underlying <video>/<audio>, for PiP, casting and metadata. */
  onElement?: (el: HTMLMediaElement | null) => void;
}

// How long a live stream may sit in `waiting` before we call it dropped.
const STALL_MS = 20_000;
// How long a freshly mounted stream gets to reach `playing` while it's
// actually trying to (not paused by an autoplay block) — streams that never
// produce a frame don't fire `waiting`, so the stall timer alone misses them.
const STARTUP_MS = 20_000;

// play() rejects with AbortError when a pause interrupts it, and with
// NotAllowedError when autoplay is blocked — neither is a stream failure
// (the native controls are still there for the user to press play).
export function isIgnorablePlayError(err: unknown): boolean {
  return err instanceof DOMException && (err.name === "AbortError" || err.name === "NotAllowedError");
}

/**
 * Wire a media element's lifecycle events to the player callbacks:
 * `playing` → onPlaying; `error` / `ended` (a live stream never ends) / a
 * long stall / never starting → onFatal.
 */
export function useMediaElementEvents(
  ref: React.RefObject<HTMLMediaElement | null>,
  cb: PlayerCallbacks,
  key: string
): void {
  const cbRef = useRef(cb);
  cbRef.current = cb;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    cbRef.current.onElement?.(el);
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    let startupTimer: ReturnType<typeof setTimeout> | undefined = setTimeout(() => {
      startupTimer = undefined;
      if (!el.paused) cbRef.current.onFatal?.({ message: "stream never started", network: false });
    }, STARTUP_MS);
    const clearStall = () => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = undefined;
    };
    const clearStartup = () => {
      if (startupTimer) clearTimeout(startupTimer);
      startupTimer = undefined;
    };
    const onPlaying = () => {
      clearStall();
      clearStartup();
      cbRef.current.onPlaying?.();
    };
    const onWaiting = () => {
      clearStall();
      stallTimer = setTimeout(() => {
        if (!el.paused) cbRef.current.onFatal?.({ message: "stream stalled", network: true });
      }, STALL_MS);
    };
    const onError = () => {
      clearStall();
      clearStartup();
      const code = el.error?.code;
      // MEDIA_ERR_NETWORK = 2; SRC_NOT_SUPPORTED (4) is what most dead
      // progressive streams surface as, which isn't worth reconnecting to.
      cbRef.current.onFatal?.({ message: el.error?.message || `media error ${code ?? "?"}`, network: code === 2 });
    };
    const onEnded = () => {
      clearStall();
      cbRef.current.onFatal?.({ message: "stream ended", network: true });
    };
    el.addEventListener("playing", onPlaying);
    el.addEventListener("waiting", onWaiting);
    el.addEventListener("error", onError);
    el.addEventListener("ended", onEnded);
    return () => {
      clearStall();
      clearStartup();
      el.removeEventListener("playing", onPlaying);
      el.removeEventListener("waiting", onWaiting);
      el.removeEventListener("error", onError);
      el.removeEventListener("ended", onEnded);
      cbRef.current.onElement?.(null);
    };
  }, [ref, key]);
}
